import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Siren, ArrowUpRight, Plus, Star } from "@phosphor-icons/react";
import { useSavedLocations } from "../lib/SavedLocationsContext";
import { checkProximity, compass } from "../lib/proximity";

// Tier thresholds (miles)
const TIERS = [
  { max: 5,   key: "imminent", color: "#E63946", label: "IMMINENT — fire is very close" },
  { max: 15,  key: "high",     color: "#F2542D", label: "HIGH — fire is nearby" },
  { max: 999, key: "watch",    color: "#FFB347", label: "WATCH — fire within your alert radius" },
];

function tierFor(mi) {
  return TIERS.find((t) => mi <= t.max) || TIERS[TIERS.length - 1];
}

export default function ProximityBanner({ onOpenManager }) {
  const { locations } = useSavedLocations();
  const [hits, setHits] = useState([]);
  const [nearest, setNearest] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (locations.length === 0) { setHits([]); setNearest(null); return; }
    let stop = false;
    const tick = async () => {
      try {
        const res = await checkProximity(
          locations.map((l) => ({ lat: l.lat, lon: l.lon, label: l.label, radius_mi: l.radius_mi }))
        );
        if (!stop) {
          setHits(res.hits || []);
          setNearest(res.nearest_overall || null);
        }
      } catch (e) {
        console.warn("ProximityBanner: proximity check failed", e);
      }
    };
    tick();
    const id = setInterval(tick, 60000);
    return () => { stop = true; clearInterval(id); };
  }, [locations]);

  // No saved locations → render the CTA bar
  if (locations.length === 0) {
    return (
      <div data-testid="proximity-cta" className="border-b border-tac-border bg-tac-surface/40">
        <button
          data-testid="proximity-cta-open"
          onClick={onOpenManager}
          className="w-full flex items-center gap-3 px-4 sm:px-6 py-2.5 text-left hover:bg-tac-surface/80 transition-colors"
        >
          <Star size={16} weight="duotone" className="text-tac-primary" />
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-tac-primary mb-0.5">
              New: Saved Location Alerts
            </div>
            <div className="text-xs text-tac-text">
              Pin your home, cabin, or trailhead and we'll warn you when fires get close.
            </div>
          </div>
          <span className="hidden sm:flex items-center gap-1.5 text-[15px] font-bold uppercase tracking-wider text-tac-dim">
            <Plus size={11} weight="bold" /> Add Location
          </span>
        </button>
      </div>
    );
  }

  if (dismissed) return null;

  if (hits.length === 0) {
    // All clear
    return (
      <div data-testid="proximity-allclear" className="border-b border-tac-border bg-tac-safe/10">
        <div className="flex items-center gap-3 px-4 sm:px-6 py-2 text-xs">
          <span className="w-1.5 h-1.5 rounded-full bg-tac-safe shadow-[0_0_6px_#2A9D8F]" />
          <span className="text-tac-safe font-bold uppercase tracking-wider text-[10px]">
            All clear
          </span>
          <span className="text-tac-dim">
            No active fires within {locations.map((l) => `${l.radius_mi} mi of ${l.label}`).join(", ")}.
          </span>
          <button
            data-testid="manage-saved-locations"
            onClick={onOpenManager}
            className="ml-auto text-[15px] font-bold uppercase tracking-wider text-tac-dim hover:text-tac-text"
          >
            Manage
          </button>
        </div>
      </div>
    );
  }

  const tier = tierFor(nearest?.distance_mi ?? 999);

  return (
    <div
      data-testid="proximity-banner"
      className="border-b-2 animate-fade-in"
      style={{
        background: `linear-gradient(90deg, ${tier.color}22, ${tier.color}11, transparent)`,
        borderBottomColor: tier.color,
      }}
    >
      <div className="px-4 sm:px-6 py-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <Siren size={20} weight="fill" style={{ color: tier.color }} className="shrink-0" />
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span
                className="text-[10px] font-bold uppercase tracking-[0.15em]"
                style={{ color: tier.color }}
              >
                {tier.label}
              </span>
            </div>
            {nearest && (
              <div className="text-sm text-tac-text">
                <Link to={`/incident/${nearest.incident_id}`} data-testid="proximity-nearest-link" className="font-display font-bold hover:underline">
                  {nearest.incident_name}
                </Link>
                <span className="text-tac-dim"> · </span>
                <span className="font-mono-tac tabular">
                  {nearest.distance_mi} mi {compass(nearest.bearing_deg)}
                </span>
                <span className="text-tac-dim"> of </span>
                <span className="font-semibold text-tac-text">{nearest.location_label}</span>
                {hits.length > 1 && (
                  <span className="text-tac-dim"> · +{hits.length - 1} more within radius</span>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 sm:ml-auto">
          <button
            data-testid="proximity-manage"
            onClick={onOpenManager}
            className="px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-wider border border-tac-border bg-tac-bg/70 hover:bg-tac-surface text-tac-text rounded-sm"
          >
            Manage
          </button>
          <button
            data-testid="proximity-dismiss"
            onClick={() => setDismissed(true)}
            className="px-2.5 py-1.5 text-[15px] font-bold uppercase tracking-wider text-tac-dim hover:text-tac-text"
          >
            Dismiss
          </button>
        </div>
      </div>
    </div>
  );
}
